import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import { getApiBaseUrl } from '../lib/apiBase';

const API_URL = getApiBaseUrl();

// Value/currency stashed by PricingPage.js's handlePayment right before the
// Stripe/PayPal redirect — Razorpay stays on-page and fires its own Purchase
// event directly, so this is only consumed here for the two redirect gateways.
const trackPurchase = (dedupeKey) => {
  if (!window.fbq) return;
  // Refresh / back-button can re-run the status check against the same
  // session_id or order id — only fire Purchase once per checkout.
  const trackedKey = `thachari_purchase_tracked_${dedupeKey}`;
  if (sessionStorage.getItem(trackedKey)) return;
  sessionStorage.setItem(trackedKey, '1');
  let meta = null;
  try {
    meta = JSON.parse(sessionStorage.getItem('thachari_purchase_meta') || 'null');
  } catch (e) {}
  sessionStorage.removeItem('thachari_purchase_meta');
  window.fbq('track', 'Purchase', {
    value: meta?.value ?? 4999,
    currency: meta?.currency || 'INR',
    content_name: 'Thachari Premium Access'
  });
};

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const attemptsRef = useRef(0);
  const headers = { Authorization: `Bearer ${token}` };

  const gateway = searchParams.get('gateway');

  useEffect(() => {
    if (!token) return;

    const finishAsUnlock = (data) => {
      // Lesson-unlock / re-access purchases redirect back here too (same
      // success_url/return_url as the lifetime plan) — send those straight
      // to the lesson/course instead of showing the "Welcome to Premium" copy.
      if (data.lesson_id) {
        navigate(`/lessons/${data.lesson_id}?unlock=success`, { replace: true });
        return true;
      }
      if (data.course_id) {
        navigate(`/courses/${data.course_id}?unlock=success`, { replace: true });
        return true;
      }
      return false;
    };

    const verifyStripe = async () => {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) {
        setErrorMsg(t('thankyou_err_missing_session', 'Missing payment session details.'));
        setStatus('error');
        return;
      }
      try {
        const { data } = await axios.get(`${API_URL}/payments/stripe/status/${sessionId}`, { headers });

        if ((data.purpose === 'lesson_unlock' || data.purpose === 'reaccess_package') && data.status === 'paid') {
          if (finishAsUnlock(data)) return;
        }

        if (data.payment_status === 'paid') {
          trackPurchase(sessionId);
          await refreshUser();
          setStatus('success');
        } else if (attemptsRef.current < 6) {
          attemptsRef.current += 1;
          setTimeout(verifyStripe, 2000);
        } else {
          setErrorMsg(t('thankyou_err_pending', 'Your payment is still being processed by the bank. If you were charged, it will be activated shortly.'));
          setStatus('error');
        }
      } catch (err) {
        setErrorMsg(err?.response?.data?.detail?.message || err?.response?.data?.detail || t('thankyou_err_generic', 'Payment verification failed. Please contact support.'));
        setStatus('error');
      }
    };

    const verifyPaypal = async () => {
      const orderId = searchParams.get('token');
      if (!orderId) {
        setErrorMsg(t('thankyou_err_missing_order', 'Missing PayPal order details.'));
        setStatus('error');
        return;
      }
      try {
        const { data } = await axios.post(`${API_URL}/payments/paypal/capture/${orderId}`, {}, { headers });

        if (data.purpose === 'lesson_unlock' || data.purpose === 'reaccess_package') {
          if (finishAsUnlock(data)) return;
        }

        if (data.status === 'COMPLETED') {
          if (!data.already_active) trackPurchase(orderId);
          await refreshUser();
          setStatus('success');
        } else {
          setErrorMsg(t('thankyou_err_pending', 'Your payment is still being processed. If you were charged, it will be activated shortly.'));
          setStatus('error');
        }
      } catch (err) {
        setErrorMsg(err?.response?.data?.detail?.message || err?.response?.data?.detail || t('thankyou_err_generic', 'Payment verification failed. Please contact support.'));
        setStatus('error');
      }
    };

    if (gateway === 'stripe') {
      verifyStripe();
    } else if (gateway === 'paypal') {
      verifyPaypal();
    } else if (gateway === 'razorpay') {
      // Razorpay stays on-page and verifies via /payments/razorpay/verify
      // before ever navigating here — by the time we land on this route the
      // payment is already confirmed, so just refresh the user and show success.
      refreshUser().finally(() => setStatus('success'));
    } else {
      // No gateway param — fall back to checking current premium status
      // (e.g. a bookmarked/shared link, or a refresh of this page).
      axios.get(`${API_URL}/auth/me`, { headers })
        .then(({ data }) => {
          if (data.is_premium) setStatus('success');
          else {
            setErrorMsg(t('thankyou_err_generic', 'Could not verify your payment. Please contact support.'));
            setStatus('error');
          }
        })
        .catch(() => {
          setErrorMsg(t('thankyou_err_generic', 'Could not verify your payment. Please contact support.'));
          setStatus('error');
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, gateway]);

  return (
    <div className="min-h-screen bg-background" data-testid="thank-you-page">
      <SEO
        title="Payment Successful — Welcome to Thachari"
        description="Your premium access is now active."
        url="/thank-you"
        noIndex={true}
      />
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-lg mx-auto px-6 py-16">
          <Card>
            <CardContent className="p-8 text-center">
              {status === 'verifying' && (
                <>
                  <Loader2 className="w-16 h-16 mx-auto text-accent animate-spin mb-6" />
                  <h1 className="text-2xl font-serif font-medium mb-2">
                    {t('thankyou_verifying_title', 'Verifying Payment')}
                  </h1>
                  <p className="text-muted-foreground">
                    {t('thankyou_verifying_desc', 'Please wait — do not close this page...')}
                  </p>
                </>
              )}

              {status === 'error' && (
                <>
                  <div className="text-6xl mb-4">⚠️</div>
                  <h1 className="text-2xl font-serif font-medium mb-2 text-destructive">
                    {t('thankyou_error_title', 'Payment Verification Failed')}
                  </h1>
                  <p className="text-muted-foreground mb-6">{errorMsg}</p>

                  <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 mb-6 text-left text-sm">
                    <p className="font-semibold mb-2">
                      {t('thankyou_error_next_steps_heading', 'What to do next:')}
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                      <li>{t('thankyou_error_step1', 'Check your bank/card for any deduction')}</li>
                      <li>{t('thankyou_error_step2', 'If money was deducted, email us with your payment ID')}</li>
                      <li>{t('thankyou_error_step3', 'We will manually verify and activate your access within 2 hours')}</li>
                    </ul>
                  </div>

                  <div className="flex flex-col gap-3">
                    <Button onClick={() => navigate('/pricing')} className="rounded-full">
                      {t('thankyou_retry_btn', '🔄 Try Again')}
                    </Button>
                    <Button asChild variant="outline" className="rounded-full">
                      <a href="mailto:support@thachari.com?subject=Payment Issue">
                        {t('thankyou_contact_btn', '✉️ Contact Support')}
                      </a>
                    </Button>
                  </div>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-50 dark:bg-green-950 border-4 border-green-500 flex items-center justify-center mb-6 text-4xl">
                    🎉
                  </div>
                  <h1 className="text-3xl font-serif font-bold mb-1">
                    {t('thankyou_success_title', 'Welcome to Thachari!')}
                  </h1>
                  <p className="text-green-600 dark:text-green-400 font-semibold mb-6">
                    {t('thankyou_success_subtitle', 'Your Premium Access is now active ✓')}
                  </p>

                  <div className="bg-secondary/30 border rounded-xl p-5 mb-6 text-left">
                    <p className="font-bold text-sm mb-3">
                      {t('thankyou_success_included_heading', 'You now have access to:')}
                    </p>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>✅ {t('thankyou_perk_courses', 'All current and future courses')}</p>
                      <p>✅ {t('thankyou_perk_certs', 'Downloadable certificates')}</p>
                      <p>✅ {t('thankyou_perk_mobile', 'Mobile app (iOS & Android)')}</p>
                      <p>✅ {t('thankyou_perk_community', 'Community projects')}</p>
                      <p>✅ {t('thankyou_perk_support', 'Priority support')}</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <Button onClick={() => navigate('/courses')} className="rounded-full">
                      {t('thankyou_start_learning_btn', '🚀 Start Learning Now')}
                    </Button>
                    <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-full">
                      {t('thankyou_dashboard_btn', 'Go to Dashboard')}
                    </Button>
                  </div>

                  <p className="mt-6 text-xs text-muted-foreground">
                    {t('thankyou_success_footer', 'Questions?')}{' '}
                    <a href="mailto:support@thachari.com" className="text-accent">
                      {t('thankyou_contact_link', 'Contact support')}
                    </a>
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
